import { Button } from "design-system/atoms/Button"
import { FilterBar, FilterBarField } from "design-system/molecules/FilterBar"
import { cn } from "design-system/utils"
import { useCompanyFilterField } from "modules/backoffice/hooks/companies/useCompanyFilterField"
import { BACKOFFICE_RECENT_TIME_FRAME_OPTIONS } from "modules/backoffice/lib/backofficeRecentTimeFrameOptions"
import { DomainSelector } from "modules/dashboard/components/Treemaps/DomainSelector"
import { TimeRangeSelector } from "modules/shared/components/TimeRange/TimeRangeSelector"
import { useFilterBarLabels } from "modules/shared/hooks/useFilterBarLabels"
import { useTranslation } from "react-i18next"
import {
  BackofficeSupportIntegrationEnum,
  BackofficeSupportIsLogisticsEnum,
  BackofficeSupportStatusEnum,
  BackofficeSupportTreemapDomainEnum,
  ChannelEnum,
  FlowEnum,
  GetBackofficeSupportQuestionsSchema,
  SupportPredefinedCategoryEnum,
  TimeDimension,
  TimeFrame,
} from "schema"
import { z } from "zod"

type SupportFilterBy = NonNullable<z.infer<typeof GetBackofficeSupportQuestionsSchema>["filterBy"]>
type TreemapDomain = (typeof BackofficeSupportTreemapDomainEnum)[number]

const INTEGRATION_LABELS = { shopify: "Shopify", bigblue: "BigBlue", amphora: "Amphora" } as const

type Props = {
  filterBy: SupportFilterBy | undefined
  onFilterChange: (changes: Partial<SupportFilterBy>) => void
  onClearFilters: () => void
  query: string
  setQuery: (query: string) => void
  selectedTimeFrame: TimeFrame
  selectedTimeDimension: TimeDimension
  onTimeRangeChange: (value: { timeFrame: TimeFrame; timeDimension: TimeDimension }) => void
  isAmphoraTicketsQuickFilterActive: boolean
  onApplyAmphoraTicketsQuickFilter: () => void
  selectedDomain: TreemapDomain
  domainOptions: TreemapDomain[]
  onDomainChange: (domain: TreemapDomain) => void
}

export function BackofficeSupportFilters(props: Props) {
  const { filterBy, onFilterChange } = props
  const { t } = useTranslation()
  const labels = useFilterBarLabels()

  const companyField = useCompanyFilterField({
    label: t("backoffice.support.filters.fields.company"),
    value: filterBy?.companyId,
    onChange: (companyId) => onFilterChange({ companyId }),
  })

  const fields: FilterBarField[] = [
    companyField,
    {
      key: "integration",
      type: "multiSelect",
      label: t("backoffice.support.filters.fields.integration"),
      value: filterBy?.integration,
      onChange: (values) =>
        onFilterChange({ integration: pickValues(BackofficeSupportIntegrationEnum, values) }),
      options: BackofficeSupportIntegrationEnum.map((integration) => ({
        value: integration,
        label: INTEGRATION_LABELS[integration],
      })),
    },
    {
      key: "channel",
      type: "select",
      label: t("backoffice.support.filters.fields.channel"),
      value: filterBy?.channel,
      onChange: (value) =>
        onFilterChange({
          channel: Object.values(ChannelEnum).find((channel) => channel === value),
        }),
      options: Object.values(ChannelEnum).map((channel) => ({
        value: channel,
        label: t(`backoffice.support.filters.channels.${channel}`),
      })),
    },
    {
      key: "flow",
      type: "select",
      label: t("backoffice.support.filters.fields.flow"),
      value: filterBy?.flow,
      onChange: (value) =>
        onFilterChange({ flow: Object.values(FlowEnum).find((flow) => flow === value) }),
      options: Object.values(FlowEnum).map((flow) => ({
        value: flow,
        label: t(`backoffice.support.filters.flows.${flow}`),
      })),
    },
    {
      key: "category",
      type: "multiSelect",
      label: t("backoffice.support.filters.fields.categories"),
      value: filterBy?.category,
      onChange: (values) =>
        onFilterChange({ category: pickValues(SupportPredefinedCategoryEnum, values) }),
      options: SupportPredefinedCategoryEnum.map((category) => ({
        value: category,
        label: category,
      })),
    },
    {
      key: "status",
      type: "select",
      label: t("backoffice.support.filters.fields.status"),
      value: filterBy?.status === "all" ? undefined : filterBy?.status,
      onChange: (value) =>
        onFilterChange({ status: BackofficeSupportStatusEnum.find((status) => status === value) }),
      options: [
        { value: "autoSolved", label: t("backoffice.support.filters.autoSolved") },
        { value: "hasTicket", label: t("backoffice.support.filters.hasTicket") },
      ],
    },
    {
      key: "isLogistics",
      type: "select",
      label: t("backoffice.support.filters.fields.questionType"),
      value: filterBy?.isLogistics === "all" ? undefined : filterBy?.isLogistics,
      onChange: (value) =>
        onFilterChange({
          isLogistics: BackofficeSupportIsLogisticsEnum.find((option) => option === value),
        }),
      options: [
        { value: "yes", label: t("backoffice.support.filters.isLogistics") },
        { value: "no", label: t("backoffice.support.filters.notLogistics") },
      ],
    },
  ]

  return (
    <div className="flex w-full flex-col gap-2 md:flex-row md:items-start">
      <DomainSelector
        value={props.selectedDomain}
        options={props.domainOptions}
        onChange={props.onDomainChange}
      />
      <FilterBar
        className="flex-1 min-w-0"
        fields={fields}
        labels={labels}
        onClearFilters={props.onClearFilters}
        searchQuery={props.query || undefined}
        onSearchQueryChange={props.setQuery}
        searchPlaceholder={t("backoffice.support.search")}
        trailing={
          <>
            <Button
              variant="outline"
              className={cn(
                "hover:bg-warning/10 hover:text-warning",
                props.isAmphoraTicketsQuickFilterActive &&
                  "bg-warning/20 text-warning hover:bg-warning/20 hover:text-warning",
              )}
              onClick={props.onApplyAmphoraTicketsQuickFilter}
            >
              {t("backoffice.support.filters.quickFilters.amphoraTickets")}
            </Button>
            <TimeRangeSelector
              value={props.selectedTimeFrame}
              dimension={props.selectedTimeDimension}
              onChange={props.onTimeRangeChange}
              presets={BACKOFFICE_RECENT_TIME_FRAME_OPTIONS}
            />
          </>
        }
      />
    </div>
  )
}

function pickValues<T extends string>(allowed: readonly T[], values: string[] | undefined) {
  const picked = allowed.filter((value) => values?.includes(value))
  return picked.length > 0 ? picked : undefined
}
